import ErrorHandler from 'goog:goog.debug.ErrorHandler';
import { Sentry } from './sentry/sentry';
import SentryFullStory from 'derivita/third_party/sentry/fullstory/src/SentryFullStory';
import type { FullStoryClient } from 'derivita/third_party/sentry/fullstory/src/types';
import { RuntimeExceptionEvent } from 'derivita/ts/analytics/events/custom_events';
import { captureProductEvent } from 'derivita/ts/analytics/events/event';
import { Level, logToProductAnalytics } from 'derivita/ts/analytics/logs/log';

// Tracks when the page is being unloaded so Sentry's beforeSend can drop
// in-flight fetch errors that fire as the browser tears down connections.
// `pagehide` fires on navigation away and tab close across all modern
// browsers (more reliable than `beforeunload`, which is fragile on mobile).
// NOTE: module-load-time side effects that reference `window` or
// `document` would break the BigQuery bundle (`omaha/interpreter/bigquery`)
// because its esbuild config has `define = {"window": "global"}` and runs
// in Node. The listener install lives inside `initLogging()` instead —
// that function is only called from app entry points.
let pageIsHiding = false;
let pagehideListenerInstalled = false;

function ensurePagehideListener(): void {
  if (pagehideListenerInstalled || typeof document === 'undefined') {
    return;
  }
  pagehideListenerInstalled = true;
  globalThis.addEventListener('pagehide', () => {
    pageIsHiding = true;
  });
  // Reset on pageshow so a BFCache restore doesn't leave us silently
  // dropping every subsequent fetch error (`event.persisted === true`
  // means this is a BFCache restore; on a fresh load the flag was
  // already false).
  globalThis.addEventListener('pageshow', () => {
    pageIsHiding = false;
  });
}

export function isTransientFetchError(event: {
  [key: string]: unknown;
}): boolean {
  const values = (event['exception'] as { [key: string]: unknown })?.[
    'values'
  ] as Array<{ [key: string]: unknown }> | undefined;
  const top = values?.[0];
  const message = (top?.['value'] as string | undefined) ?? '';
  // Covers `TypeError: Failed to fetch` (Chromium), `TypeError: Load failed`
  // (Safari), `TypeError: NetworkError when attempting to fetch resource`
  // (Firefox), the hrpc UNAVAILABLE wrappers around those, and Firebase Auth
  // failing to reach identitytoolkit.googleapis.com. beforeSend drops these
  // only while the page is hiding or offline.
  return (
    message.includes('Failed to fetch') ||
    message.includes('Load failed') ||
    message.includes('NetworkError when attempting to fetch resource') ||
    message === 'Network error' ||
    message.includes('auth/network-request-failed')
  );
}

// Options used when re-registering Sentry's browser-API wrapper. Closure's
// ErrorHandler already covers the three timer APIs, so we disable them here
// and only ask Sentry to wrap the surfaces Closure cannot.
const BROWSER_API_WRAPPER_OPTIONS = {
  'setTimeout': false,
  'setInterval': false,
  'requestAnimationFrame': false,
  'XMLHttpRequest': true,
  'eventTarget': true,
};

/**
 * Builds the Sentry integrations array for `Sentry.init`. Exported so unit
 * tests can exercise the filter + re-registration logic with fabricated
 * inputs.
 *
 * - Filters the default `TryCatch` (v6/v7) and `BrowserApiErrors` (v8)
 *   integrations out so they don't double-wrap timer APIs with Closure's
 *   `goog.debug.ErrorHandler.protectWindow*`.
 * - Re-registers a variant that covers only XHR + addEventListener,
 *   preferring the v7 class form (`Sentry.Integrations.TryCatch`) and
 *   falling back to the v8 factory form (`Sentry.browserApiErrorsIntegration`)
 *   so a future SDK bump doesn't silently drop coverage.
 * - Adds our FullStory bridge when the FS client is present.
 */
export function buildIntegrations(
  defaults: Array<{ [key: string]: unknown }>,
  sentryGlobal: unknown,
  fsClient: unknown
): unknown[] {
  const integrations: unknown[] = defaults.filter(
    (i) => i['name'] !== 'TryCatch' && i['name'] !== 'BrowserApiErrors'
  );
  const wrapper = makeBrowserApiWrapper(sentryGlobal);
  if (wrapper) {
    integrations.push(wrapper);
  }
  if (fsClient) {
    integrations.push(
      new SentryFullStory('derivita', {
        'client': fsClient as FullStoryClient,
      })
    );
  }
  return integrations;
}

function makeBrowserApiWrapper(sentryGlobal: unknown): unknown {
  const sentry = sentryGlobal as { [key: string]: unknown } | undefined;
  // v6/v7: new Sentry.Integrations.TryCatch(options)
  const integrationsNamespace = sentry?.['Integrations'] as
    | { [key: string]: unknown }
    | undefined;
  const TryCatchCtor = integrationsNamespace?.['TryCatch'] as
    | (new (opts: typeof BROWSER_API_WRAPPER_OPTIONS) => unknown)
    | undefined;
  if (TryCatchCtor) {
    return new TryCatchCtor(BROWSER_API_WRAPPER_OPTIONS);
  }
  // v8+: Sentry.browserApiErrorsIntegration(options) — functional form.
  const browserApiErrorsIntegration = sentry?.[
    'browserApiErrorsIntegration'
  ] as ((opts: typeof BROWSER_API_WRAPPER_OPTIONS) => unknown) | undefined;
  if (browserApiErrorsIntegration) {
    return browserApiErrorsIntegration(BROWSER_API_WRAPPER_OPTIONS);
  }
  return undefined;
}

/** Tags that let Sentry events be filtered by customer, role and app. */
export interface SentryTags {
  /** The client-events service name, e.g. `omaha-student`. */
  readonly app?: string;
  /** `instructor`: a teacher or course admin; `author`: other authoring users. */
  readonly role?: 'student' | 'instructor' | 'author';
  /**
   * The LTI consumer key / Firebase `org` claim, e.g. `math@school.org`.
   * Usually an institution address, but a customer may be one teacher's.
   */
  readonly customer?: string;
}

function sentryTagValues(tags: SentryTags): { [key: string]: string } {
  const values: { [key: string]: string } = {};
  if (tags.app) {
    values['app'] = tags.app;
  }
  if (tags.role) {
    values['role'] = tags.role;
  }
  if (tags.customer) {
    values['customer'] = tags.customer;
  }
  return values;
}

function setSentryTags(tags: SentryTags): void {
  const values = sentryTagValues(tags);
  if (Object.keys(values).length > 0) {
    Sentry.setTags(values);
  }
}

/**
 * Adds tags to every later Sentry event, for values a page learns after
 * `initLogging`. Call it after `initLogging`, so its `Sentry.onLoad` callback
 * is queued behind the one that calls `Sentry.init`.
 */
export function tagSentryEvents(tags: SentryTags): void {
  Sentry.onLoad(() => setSentryTags(tags));
}

/**
 * Reads the `org` claim from a Firebase custom token minted by our servers.
 * The token is decoded, not verified: it is only used to tag Sentry events,
 * and reading it needs no network, so it works when Firebase Auth is blocked.
 */
export function customerFromCustomToken(
  token: string | undefined
): string | undefined {
  const payload = token?.split('.')[1];
  if (!payload) {
    return undefined;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const decoded = JSON.parse(new TextDecoder().decode(bytes)) as {
      [key: string]: { [key: string]: unknown } | undefined;
    };
    const org = decoded['claims']?.['org'];
    return typeof org === 'string' && org ? org : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads the consumer key a server template renders into
 * `<meta name="sentry-customer">`, for pages that have no custom token.
 */
export function customerFromPageMeta(): string | undefined {
  const meta = document.querySelector('meta[name="sentry-customer"]');
  return meta?.getAttribute('content') || undefined;
}

export function initLogging(session?: string, tags: SentryTags = {}) {
  ensurePagehideListener();
  Sentry.onLoad(() => {
    Sentry.init({
      'release': Sentry.getReleaseName('omaha-'),
      'environment': window.location.host,
      'ignoreErrors': [
        'ResizeObserver loop completed with undelivered notifications',
        'ResizeObserver loop limit exceeded',
        'UnknownError: Database deleted by request of the user',
        'UnknownError: Attempt to get records from database without an in-progress transaction',
        // TrustedTypes policy callback invoked after teardown during page unload
        'The provided callback is no longer runnable',
        // Transient browser-initiated IDB teardown (e.g. Safari); not actionable.
        'Connection to Indexed Database server lost',
        // Transient fetch/navigation aborts — not actionable bugs (see #3696).
        'AbortError',
        'The user aborted a request',
      ],
      'denyUrls': ['www.scrible.com'],
      // Split wrapper ownership with Closure's goog.debug.ErrorHandler:
      //   - Closure owns setTimeout / setInterval / requestAnimationFrame
      //     via protectWindow* (see logErrors below). Stacking Sentry's
      //     wrappers on top of those produced the infinite-recursion loop
      //     (DERIVITA-R25 / R23 / R24 / R40 / VN0 / RJK).
      //   - Sentry owns XMLHttpRequest and addEventListener — Closure has
      //     no equivalent, and GlobalHandlers alone only captures the
      //     throw site, not the listener registration site. Re-enabling
      //     just those two flags on TryCatch closes the coverage gap.
      // Since the two sides now patch disjoint APIs there's no double-wrap.
      'integrations': (defaults: Array<{ [key: string]: unknown }>) =>
        buildIntegrations(defaults, goog.global['Sentry'], goog.global['FS']),
      // Drop transient network errors that fire while the page is unloading
      // or while the browser reports offline. These are not actionable bugs
      // and previously made up the bulk of DERIVITA-TK9 / 4Q (160+ events).
      // Real backend outages are still reported when the page is visible
      // and the network is up.
      'beforeSend': (event: { [key: string]: unknown }) => {
        if (
          isTransientFetchError(event) &&
          (pageIsHiding || navigator.onLine === false)
        ) {
          return null;
        }
        return event;
      },
    });
    if (session !== undefined) {
      Sentry.setContext('session', { 'id': session });
    }
    setSentryTags(tags);
    const traceMeta = getPageTraceMeta();
    if (traceMeta) {
      const projectParam = traceMeta.project
        ? `project=${traceMeta.project}&`
        : '';
      Sentry.setContext('gcp_trace', {
        'trace_id': traceMeta.traceId,
        'trace_url': `https://console.cloud.google.com/traces/list?${projectParam}tid=${traceMeta.traceId}`,
      });
    }
  });
  if (session !== undefined) {
    logToProductAnalytics(`session ${session}`, Level.LOG);
  }
}

/** Reads the GCP trace metadata injected by the server into a meta tag. */
function getPageTraceMeta():
  | { traceId: string; project: string | undefined }
  | undefined {
  const meta = document.querySelector('meta[name="trace-id"]');
  if (!meta) {
    return undefined;
  }
  const traceId = meta.getAttribute('content');
  if (!traceId) {
    return undefined;
  }
  const project = meta.getAttribute('data-project') || undefined;
  return { traceId, project };
}

/**
 * Reports a Firestore listener's error callback (e.g. `unavailable`,
 * `permission-denied`), which otherwise leaves no server-side record.
 * `unavailable` is skipped while the page is hiding or offline. Returns
 * whether the error was sent.
 */
export function logFirestoreListenerError(
  error: unknown,
  path: string
): boolean {
  const code = (error as { [key: string]: unknown } | null | undefined)?.[
    'code'
  ];
  if (
    code === 'unavailable' &&
    (pageIsHiding || globalThis.navigator?.onLine === false)
  ) {
    return false;
  }
  Sentry.captureException(
    error instanceof Error ? error : new Error(String(error)),
    {
      'tags': { 'firestore.code': typeof code === 'string' ? code : 'unknown' },
      'contexts': { 'firestore_listener': { 'path': path } },
    }
  );
  return true;
}

export function logError(error: any, debug = false) {
  // Auto-wrap strings in Error objects so Sentry gets a stacktrace.
  // Passing strings to captureException results in <unknown> issues with no
  // stacktrace, making them impossible to triage.
  if (typeof error === 'string') {
    error = new Error(error);
  }
  Sentry.captureException(error);
  logUserError(error, debug);
}

//logUserError skips logging to sentry since the issue is caused by user actions not system errors
export function logUserError(error: any, debug = false) {
  const errorMessage = error.toString();

  logToProductAnalytics(errorMessage, Level.ERROR);
  captureProductEvent(new RuntimeExceptionEvent(errorMessage));
  if (debug) {
    console.error(error);
  }
}

// Lazily-initialized singleton so protectWindow* only mutates window globals
// once per page, no matter how many logErrors() call sites fire. Re-running
// protectWindowSetTimeout on each call produces an ever-growing wrapper chain
// that eventually self-references (see DERIVITA-R25 / R23 / R24 / R40 / VN0 /
// RJK "too much recursion").
let sharedErrorHandler: ErrorHandler | undefined;

function getErrorHandler(): ErrorHandler {
  if (!sharedErrorHandler) {
    sharedErrorHandler = new ErrorHandler((error: unknown) => logError(error));
    sharedErrorHandler.protectWindowRequestAnimationFrame();
    sharedErrorHandler.protectWindowSetInterval();
    sharedErrorHandler.protectWindowSetTimeout();
  }
  return sharedErrorHandler;
}

export function logErrors(cb: () => void | Promise<void>, debug = false) {
  if (goog.DEBUG) {
    // no error handling
    cb();
    return;
  }
  const handler = (error: unknown) => logError(error, debug);
  const maybePromise = getErrorHandler().wrap(cb)();

  // if the callback itself is async, we can watch for rejections in its
  // evaluation and handle them. unfortunately, this doesn't protect
  // against unhandled rejections in un-awaited promises. Newer closure
  // versions (v20190819 or newer) have a `catchUnhandledRejections()`
  // method on the error handler, but we're on v20171203.
  if (maybePromise instanceof Promise) {
    maybePromise.catch(handler);
  }
}
